// src/components/NotificationPreferencesPanel.tsx
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { CalendarClock, MessageCircle, Megaphone, TriangleAlert } from "lucide-react";
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  REMINDER_OPTIONS,
  bootstrapNotificationPreferences,
  updateNotificationPreferences,
  type NotificationCategory,
  type NotificationPreferences,
} from "../services/notificationPreferences";
import {
  cancelNotifications,
  getPendingNotificationIds,
  hasNotificationPermission,
  isNotificationsSupported,
  requestNotificationPermission,
} from "../services/localNotifications";
import { registerForPush, removePushToken } from "../services/pushNotifications";
import i18n from "../i18n";

function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400
        ${checked ? "bg-orange-600" : "bg-gray-300 dark:bg-gray-700"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

const CATEGORY_META: Array<{
  key: NotificationCategory;
  icon: JSX.Element;
  titleKey: string;
  descKey: string;
  webNoteKey?: string;
}> = [
  {
    key: "matches",
    icon: <CalendarClock className="w-5 h-5" />,
    titleKey: "notifications:categories.matches.title",
    descKey: "notifications:categories.matches.desc",
    webNoteKey: "notifications:categories.matches.webNote",
  },
  {
    key: "chat",
    icon: <MessageCircle className="w-5 h-5" />,
    titleKey: "notifications:categories.chat.title",
    descKey: "notifications:categories.chat.desc",
  },
  {
    key: "posts",
    icon: <Megaphone className="w-5 h-5" />,
    titleKey: "notifications:categories.posts.title",
    descKey: "notifications:categories.posts.desc",
  },
];

export default function NotificationPreferencesPanel() {
  const { t } = useI18nSafe();
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const supported = isNotificationsSupported();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [p, granted] = await Promise.all([
        bootstrapNotificationPreferences(),
        supported ? hasNotificationPermission() : Promise.resolve(true),
      ]);
      if (cancelled) return;
      setPrefs(p);
      setPermissionDenied(p.enabled && !granted);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  async function handleMasterToggle(next: boolean) {
    if (next && supported) {
      const [localGranted, pushGranted] = await Promise.all([
        requestNotificationPermission(),
        registerForPush(),
      ]);
      const granted = localGranted || pushGranted;
      setPermissionDenied(!granted);
      if (!granted) return;
    }
    if (!next) {
      const pendingIds = await getPendingNotificationIds();
      await Promise.all([cancelNotifications(Array.from(pendingIds)), removePushToken()]);
    }
    const updated = await updateNotificationPreferences({ enabled: next });
    setPrefs(updated);
  }

  async function handleCategoryToggle(category: NotificationCategory, next: boolean) {
    const updated = await updateNotificationPreferences({
      categories: { ...prefs.categories, [category]: next },
    });
    setPrefs(updated);
  }

  async function handleReminderChange(minutes: number) {
    const updated = await updateNotificationPreferences({ reminderMinutes: minutes });
    setPrefs(updated);
  }

  if (loading) return null;

  return (
    <div>
      {!supported && (
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
          <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t("notifications:web_notice")}</span>
        </div>
      )}

      {permissionDenied && (
        <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-800 dark:text-red-200">
          <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t("notifications:permission_denied")}</span>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800 overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <div>
            <div className="font-medium">{t("notifications:master.title")}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{t("notifications:master.desc")}</div>
          </div>
          <Toggle
            checked={prefs.enabled}
            onChange={handleMasterToggle}
            label={t("notifications:master.title")}
          />
        </div>

        {CATEGORY_META.map(({ key, icon, titleKey, descKey, webNoteKey }) => (
          <div key={key} className="flex items-center justify-between p-4">
            <div className="flex items-start gap-3">
              <span className="text-orange-600 mt-0.5">{icon}</span>
              <div>
                <div className="font-medium">{t(titleKey)}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{t(descKey)}</div>
                {webNoteKey && !Capacitor.isNativePlatform() && (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">{t(webNoteKey)}</div>
                )}
              </div>
            </div>
            <Toggle
              checked={prefs.enabled && prefs.categories[key]}
              disabled={!prefs.enabled}
              onChange={(v) => handleCategoryToggle(key, v)}
              label={t(titleKey)}
            />
          </div>
        ))}

        <div className="p-4">
          <label className="block font-medium mb-1" htmlFor="reminder-delay">
            {t("notifications:reminder.title")}
          </label>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t("notifications:reminder.desc")}</p>
          <select
            id="reminder-delay"
            disabled={!prefs.enabled || !prefs.categories.matches}
            value={prefs.reminderMinutes}
            onChange={(e) => handleReminderChange(Number(e.target.value))}
            className="w-full sm:w-64 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 disabled:opacity-50"
          >
            {REMINDER_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes < 60
                  ? t("notifications:reminder.minutes", { count: minutes })
                  : minutes === 1440
                  ? t("notifications:reminder.oneDay")
                  : t("notifications:reminder.hours", { count: minutes / 60 })}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
